import { useEffect, useMemo } from 'react';

import {
  TemplateStudioBlock,
  templateStudioStateService
} from 'state/template-studio.state.service';

import { mapBlockToSecondarySidebar } from 'components/TemplatesStudio/Components/FormPage/utils/blockForms.utils';

import { useObservable } from '../observable';

export const useTemplateStudioState = () => {
  const openSidebarBlock = useObservable(
    templateStudioStateService.openSidebarBlock
  );
  const openSecondarySidebarBlock = useObservable(
    templateStudioStateService.openSecondarySidebarBlock
  );
  const hiddenBlocks = useObservable(templateStudioStateService.hiddenBlocks);
  const previewDeviceLayout = useObservable(
    templateStudioStateService.previewDeviceLayout
  );
  const templateIdToActivate = useObservable(
    templateStudioStateService.templateIdToActivate
  );
  const templateIdToDelete = useObservable(
    templateStudioStateService.templateIdToDelete
  );
  const currentCompany = useObservable(
    templateStudioStateService.currentCompany
  );
  const defaultBodyBlocksOrder = useObservable(
    templateStudioStateService.defaultBodyBlocksOrder
  );
  const supportedThemes = useObservable(
    templateStudioStateService.supportedThemes
  );
  const supportedThemesForBundles = useObservable(
    templateStudioStateService.supportedThemesForBundles
  );
  const supportedThemesForPromotions = useObservable(
    templateStudioStateService.supportedThemesForPromotions
  );
  const supportedThemesForRollingOffers = useObservable(
    templateStudioStateService.supportedThemesForRollingOffers
  );
  const relevantBundleDesigns = useObservable(
    templateStudioStateService.relevantBundleDesigns
  );
  const displayPopupPreview = useObservable(
    templateStudioStateService.displayPopupPreview
  );
  const relevantPromotionDesigns = useObservable(
    templateStudioStateService.relevantPromotionDesigns
  );
  const relevantRollingOffersDesigns = useObservable(
    templateStudioStateService.relevantRollingOffersDesigns
  );
  const relevantDailyBonusBadgesDesigns = useObservable(
    templateStudioStateService.relevantDailyBonusBadgesDesigns
  );
  const relevantPopupsDesigns = useObservable(
    templateStudioStateService.relevantPopupsDesigns
  );
  const relevantProgressBarDesigns = useObservable(
    templateStudioStateService.relevantProgressBarDesigns
  );
  const scrollToSection = useObservable(
    templateStudioStateService.scrollToSection
  );
  const isBrandKitOpen = useObservable(
    templateStudioStateService.isBrandKitOpen
  );
  const isPersonalizationOpen = useObservable(
    templateStudioStateService.isPersonalizationOpen
  );
  const personalizationJson = useObservable(
    templateStudioStateService.personalizationJson
  );
  const generatedOffersJson = useObservable(
    templateStudioStateService.generatedOffersJson
  );
  const previewOffersResponse = useObservable(
    templateStudioStateService.previewOffersResponse
  );
  const personalizationPanelWidth = useObservable(
    templateStudioStateService.personalizationPanelWidth
  );

  const BlockSecondarySidebar = useMemo(
    () =>
      openSecondarySidebarBlock
        ? mapBlockToSecondarySidebar[openSecondarySidebarBlock]
        : undefined,
    [openSecondarySidebarBlock]
  );

  const isSecondarySidebarOpen = useMemo(
    () => !!BlockSecondarySidebar,
    [BlockSecondarySidebar]
  );

  const setOpenSidebarBlock = (block: TemplateStudioBlock | undefined) => {
    templateStudioStateService.setOpenSidebarBlock(block);
  };

  const setOpenSecondarySidebarBlock = (
    block: TemplateStudioBlock | undefined
  ) => {
    templateStudioStateService.setOpenSecondarySidebarBlock(block);
  };

  const setIsBrandKitOpen = (isOpen: boolean) => {
    templateStudioStateService.setIsBrandKitOpen(isOpen);
  };

  const setIsPersonalizationOpen = (isOpen: boolean) => {
    templateStudioStateService.setIsPersonalizationOpen(isOpen);
  };

  const setPersonalizationJson = (json: string | undefined) => {
    templateStudioStateService.setPersonalizationJson(json);
  };

  const resetSidebars = () => {
    templateStudioStateService.setOpenSidebarBlock(undefined);
    templateStudioStateService.setOpenSecondarySidebarBlock(undefined);
  };

  return {
    openSidebarBlock,
    setOpenSidebarBlock,
    openSecondarySidebarBlock,
    setOpenSecondarySidebarBlock,
    hiddenBlocks,
    toggleBlockVisibility: templateStudioStateService.toggleBlockVisibility,
    isBlockHidden: templateStudioStateService.isBlockHidden,
    previewDeviceLayout,
    togglePreviewDeviceLayout:
      templateStudioStateService.togglePreviewDeviceLayout,
    templateIdToActivate,
    setTemplateIdToActivate: templateStudioStateService.setTemplateIdToActivate,
    templateIdToDelete,
    setTemplateIdToDelete: templateStudioStateService.setTemplateIdToDelete,
    currentCompany,
    setCurrentCompany: templateStudioStateService.setCurrentCompany,
    defaultBodyBlocksOrder,
    setDefaultBodyBlocksOrder:
      templateStudioStateService.setDefaultBodyBlocksOrder,
    displayPopupPreview,
    setDisplayPopupPreview: templateStudioStateService.setDisplayPopupPreview,
    displayProgressBarPreview:
      templateStudioStateService.displayProgressBarPreview,
    setDisplayProgressBarPreview:
      templateStudioStateService.setDisplayProgressBarPreview,
    supportedThemes,
    setSupportedThemes: templateStudioStateService.setSupportedThemes,
    supportedThemesForBundles,
    setSupportedThemesForBundles:
      templateStudioStateService.setSupportedThemesForBundles,
    supportedThemesForPromotions,
    setSupportedThemesForPromotions:
      templateStudioStateService.setSupportedThemesForPromotions,
    supportedThemesForRollingOffers,
    setSupportedThemesForRollingOffers:
      templateStudioStateService.setSupportedThemesForRollingOffers,
    relevantBundleDesigns,
    setRelevantBundleDesigns:
      templateStudioStateService.setRelevantBundleDesigns,
    relevantPromotionDesigns,
    setRelevantPromotionDesigns:
      templateStudioStateService.setRelevantPromotionDesigns,
    relevantRollingOffersDesigns,
    setRelevantRollingOffersDesigns:
      templateStudioStateService.setRelevantRollingOffersDesigns,
    relevantDailyBonusBadgesDesigns,
    setRelevantDailyBonusBadgesDesigns:
      templateStudioStateService.setRelevantDailyBonusBadgesDesigns,
    relevantPopupsDesigns,
    setRelevantPopupsDesigns:
      templateStudioStateService.setRelevantPopupsDesigns,
    relevantProgressBarDesigns,
    setRelevantProgressBarDesigns:
      templateStudioStateService.setRelevantProgressBarDesigns,
    scrollToSection,
    setScrollToSection: templateStudioStateService.setScrollToSection,
    isBrandKitOpen,
    setIsBrandKitOpen,
    isPersonalizationOpen,
    setIsPersonalizationOpen,
    personalizationJson,
    setPersonalizationJson,
    generatedOffersJson,
    setGeneratedOffersJson: templateStudioStateService.setGeneratedOffersJson,
    previewOffersResponse,
    setPreviewOffersResponse:
      templateStudioStateService.setPreviewOffersResponse,
    personalizationPanelWidth,
    setPersonalizationPanelWidth:
      templateStudioStateService.setPersonalizationPanelWidth,
    BlockSecondarySidebar,
    isSecondarySidebarOpen,
    resetSidebars
  };
};
